// src/pages/DashboardPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnnouncements, getUpcomingBirthdays } from '../api';
import { useAuth } from '../context/AuthContext';
import NavBar from '../components/shared/NavBar';

export default function DashboardPage() {
  const navigate                        = useNavigate();
const { member, role, boardingHouse } = useAuth();

  const [announcements, setAnnouncements] = useState([]);
  const [birthdays,     setBirthdays]     = useState([]);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [annResult, bdayResult] = await Promise.all([
          getAnnouncements(),
          getUpcomingBirthdays()
        ]);
        setAnnouncements(annResult.announcements?.slice(0, 5) || []);
        setBirthdays(bdayResult.upcoming || []);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const NAVY = '#1B3A6B';

  const getRoleBadge = () => {
    const badges = {
      executive:  { label: 'Executive',     bg: '#fdecea', color: '#c0392b' },
      treasurer:  { label: 'Treasurer',     bg: '#fff3cd', color: '#856404' },
      house_rep:  { label: 'House Rep',     bg: '#d4edda', color: '#1a6b3a' },
      member:     { label: 'Member',        bg: '#d6eaf8', color: '#1B3A6B' }
    };
    return badges[role] || badges.member;
  };

  const badge = getRoleBadge();

  const todayBirthdays   = birthdays.filter(b => b.isToday);
  const upcomingBirthdays = birthdays.filter(b => !b.isToday);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Top Navigation Bar */}
      <NavBar />

      {/* Main Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Welcome Banner */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '24px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              Welcome back, {member?.name?.split(' ')[0] || 'Member'}!
            </h2>
            <p style={{ margin: '0 0 8px', opacity: 0.85, fontSize: '14px' }}>
              {boardingHouse} House
            </p>
            <span style={{
              background: badge.bg, color: badge.color,
              padding: '3px 10px', borderRadius: '12px',
              fontSize: '12px', fontWeight: 'bold'
            }}>
              {badge.label}
            </span>
          </div>
          <div style={{ textAlign: 'right', opacity: 0.85, fontSize: '13px' }}>
            <div>{new Date().toLocaleDateString('en-GB', {
              weekday: 'long', day: 'numeric',
              month: 'long', year: 'numeric'
            })}</div>
          </div>
        </div>

        {/* Today's Birthdays Banner */}
        {todayBirthdays.length > 0 && (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #C9A84C',
            borderRadius: '12px', padding: '16px 20px',
            marginBottom: '24px', textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎂</div>
            <div style={{ fontWeight: 'bold', color: '#856404', fontSize: '16px' }}>
              {todayBirthdays.map(b => b.name).join(' & ')} —
              Happy Birthday! 🎉
            </div>
            <div style={{ color: '#856404', fontSize: '13px', marginTop: '4px' }}>
              From the entire Achimota 1992 Year Group
            </div>
          </div>
        )}

        <div className="dash-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Announcements */}
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{ color: NAVY, margin: '0 0 16px', fontSize: '16px' }}>
              📢 Latest Announcements
            </h3>
            {loading ? (
              <p style={{ color: '#999', fontSize: '14px' }}>Loading...</p>
            ) : announcements.length === 0 ? (
              <p style={{ color: '#999', fontSize: '14px' }}>No announcements yet.</p>
            ) : (
              announcements.map(ann => (
                <div key={ann.id} style={{
                  borderBottom: '1px solid #f0f0f0',
                  paddingBottom: '12px', marginBottom: '12px'
                }}>
                  {ann.priority === 'urgent' && (
                    <span style={{
                      background: '#fdecea', color: '#c0392b',
                      fontSize: '10px', fontWeight: 'bold',
                      padding: '2px 6px', borderRadius: '4px',
                      marginRight: '6px'
                    }}>URGENT</span>
                  )}
                  <div style={{
                    fontWeight: '600', fontSize: '14px',
                    color: '#333', marginBottom: '4px'
                  }}>
                    {ann.title}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {new Date(ann.date).toLocaleDateString('en-GB')}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Upcoming Birthdays */}
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{ color: NAVY, margin: '0 0 16px', fontSize: '16px' }}>
              🎂 Upcoming Birthdays
            </h3>
            {loading ? (
              <p style={{ color: '#999', fontSize: '14px' }}>Loading...</p>
            ) : upcomingBirthdays.length === 0 ? (
              <p style={{ color: '#999', fontSize: '14px' }}>
                No birthdays in the next 7 days.
              </p>
            ) : (
              upcomingBirthdays.map((b, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                      {b.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {b.boardingHouse} House
                    </div>
                  </div>
                  <span style={{
                    background: '#e8eff8', color: NAVY,
                    padding: '3px 8px', borderRadius: '10px',
                    fontSize: '12px', fontWeight: '600'
                  }}>
                    {b.daysUntil === 1 ? 'Tomorrow' : `In ${b.daysUntil} days`}
                  </span>
                </div>
              ))
            )}
          </div>

        </div>

        {/* Quick Links */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          padding: '20px', marginTop: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <h3 style={{ color: NAVY, margin: '0 0 16px', fontSize: '16px' }}>
            Quick Links
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '12px'
          }}>
            {[
              { label: '👤 My Profile',      path: '/profile' },
              { label: '👥 Directory',        path: '/directory' },
              { label: '💰 My Dues',          path: '/dues' },
              { label: '📢 Announcements',    path: '/announcements' },
              { label: '🤝 Opportunities',    path: '/opportunities' },
              { label: '🖼️ Activities',  path: '/activities' },
              { label: '🎯 Fundraising',      path: '/fundraising' },
              ...(role === 'treasurer' || role === 'executive'
              ? [{ label: '💳 Record Payment', path: '/payments/enter' }]
              : []),
              ...(role === 'executive' || role === 'treasurer'
              ? [{ label: '📊 Exec Dashboard', path: '/exec-dashboard' }]
              : []),
              ...(role === 'executive'
              ? [{ label: '⚙️ Manage Members', path: '/admin/members' }]
              : [])
              ].map(link => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                style={{
                  background: '#f0f4f8', border: 'none',
                  borderRadius: '8px', padding: '14px 10px',
                  cursor: 'pointer', fontSize: '13px',
                  fontWeight: '600', color: NAVY,
                  textAlign: 'center', transition: 'background 0.2s'
                }}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}